import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, LoadingBlock } from '../ui.tsx';
import { formatLocalTime } from '../../i18n/datetime.ts';
import { buildDayRoute, directionsToAddress, directionsToAll, type DayStop } from '@shared/day-route.ts';
import { routeMapProvider, type RouteMapChoice } from '@shared/route-navigation.ts';
import type { Appointment } from '@shared/types/index.ts';

/**
 * The maps app this browser chose on the old Day map route card, when it chose
 * one. Kept so that choice still holds; with none saved, Apple Maps on Apple
 * devices and Google Maps everywhere else.
 */
function readRouteMapChoice(): RouteMapChoice {
  try {
    const value = localStorage.getItem('blastoclean:route-map-provider');
    if (value === 'google' || value === 'apple' || value === 'waze') return value;
  } catch {
    /* Storage is optional. */
  }
  return 'auto';
}

const PROVIDER_NAMES = { google: 'Google Maps', apple: 'Apple Maps', waze: 'Waze' } as const;

function todayIn(timezone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone ?? 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Today's visits assigned to the signed-in person, in booked order, with one
 * button that opens directions to the next stop from wherever they are now.
 *
 * Works for every role: crew see the visits the server already limits them
 * to; everyone else asks for their own assignments. No route settings, no
 * map page and no routing provider are involved.
 *
 * `compact` is the home-screen version: the button, the next stop and a link
 * to the full list. It renders nothing on a day with no visits, so people who
 * never go out on jobs never see it.
 */
export default function TodayRoute({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { currentLocation: location, user } = useSession();
  const crew = user?.role === 'crew';
  const date = todayIn(location?.timezone);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const query = useQuery({
    // Under 'appointments' so every visit change that refreshes the schedule
    // refreshes this too. It also re-reads every minute and on returning to
    // the tab (the app turns focus refetch off by default): people leave it
    // open while they drive, and dispatch can cancel or add a stop meanwhile.
    queryKey: ['appointments', 'day-route', location?.id, user?.id, date],
    enabled: !!user,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: () =>
      api.get<{ items: Appointment[]; truncated: boolean }>(
        `/schedule/appointments?from=${date}&to=${date}` +
          (crew ? '' : `&assignedUserId=${encodeURIComponent(user!.id)}`),
      ),
  });
  const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
  const provider = routeMapProvider(readRouteMapChoice(), userAgent);
  const visitLink = (id: string) =>
    crew ? `/my-jobs/${encodeURIComponent(id)}` : `/schedule?appointment=${encodeURIComponent(id)}`;

  if (query.isLoading) return compact ? null : <LoadingBlock label={t('todayRoute.loading')} />;
  if (query.isError)
    return compact ? null : (
      <Alert tone="error" title={t('todayRoute.loadError')}>
        {query.error.message}
      </Alert>
    );
  const route = buildDayRoute(query.data?.items ?? [], now);
  if (compact && route.stops.length === 0) return null;

  const next = route.next;
  const nextUrl = next ? directionsToAddress(next.address, provider) : null;
  const ahead = route.stops.filter((stop) => stop.state === 'next' || stop.state === 'upcoming');
  const allUrl =
    ahead.length >= 2
      ? directionsToAll(
          ahead.map((stop) => stop.address),
          provider,
          { userAgent, touchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints },
        )
      : null;
  const started = route.done > 0 || route.stops.some((stop) => stop.state === 'missed');

  return (
    <section className="card mb-6 flex flex-col gap-3" aria-labelledby="today-route-title">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="today-route-title" className="text-lg font-bold">
          {t('todayRoute.title')}
        </h2>
        {route.stops.length > 0 && (
          <p className="text-sm">{t('todayRoute.left', { remaining: route.remaining, total: route.stops.length })}</p>
        )}
      </div>
      {query.data?.truncated && <Alert>{t('todayRoute.truncated')}</Alert>}
      {route.stops.length === 0 && (
        <div>
          <p className="font-semibold">{t('todayRoute.empty')}</p>
          <p className="text-sm">{t('todayRoute.emptyDetail')}</p>
        </div>
      )}
      {next && (
        <div className="flex flex-col gap-2">
          <p>
            <span className="font-semibold">
              {t('todayRoute.nextLine', {
                number: next.number,
                customer: next.visit.customerName,
                time: formatLocalTime(next.visit.startTimeLocal),
              })}
            </span>
            {next.address && <span className="block text-sm">{next.address}</span>}
          </p>
          {nextUrl ? (
            <a className="btn btn-primary self-start" href={nextUrl} target="_blank" rel="noopener noreferrer">
              {started ? t('todayRoute.navigateNext') : t('todayRoute.start')}
            </a>
          ) : (
            <Alert>
              {t('todayRoute.noAddress')} <Link to={visitLink(next.visit.id)}>{t('todayRoute.openVisit')}</Link>
            </Alert>
          )}
          <p className="text-sm">{t('todayRoute.fromHere', { provider: PROVIDER_NAMES[provider] })}</p>
        </div>
      )}
      {!next && route.stops.length > 0 && (
        <p className="font-semibold">{route.missed > 0 ? t('todayRoute.noneAhead') : t('todayRoute.allDone')}</p>
      )}
      {allUrl && (
        <a className="btn btn-secondary self-start" href={allUrl} target="_blank" rel="noopener noreferrer">
          {t('todayRoute.openAll', { count: ahead.length, provider: PROVIDER_NAMES[provider] })}
        </a>
      )}
      {compact && !crew && (
        <Link className="self-start text-sm underline" to="/today">
          {t('todayRoute.seeAll')}
        </Link>
      )}
      {!compact && route.stops.length > 0 && (
        <ol className="flex flex-col gap-2">
          {route.stops.map((stop) => (
            <StopRow key={stop.visit.id} stop={stop} provider={provider} href={visitLink(stop.visit.id)} />
          ))}
        </ol>
      )}
    </section>
  );
}

function StopRow({
  stop,
  provider,
  href,
}: {
  stop: DayStop<Appointment>;
  provider: ReturnType<typeof routeMapProvider>;
  href: string;
}) {
  const { t } = useTranslation();
  const open = stop.state === 'next' || stop.state === 'upcoming';
  const url = open ? directionsToAddress(stop.address, provider) : null;
  return (
    <li className={`flex flex-wrap items-center gap-3 rounded border p-3${stop.state === 'done' ? ' opacity-70' : ''}`}>
      <span className="w-6 text-center font-bold" aria-hidden="true">
        {stop.number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {formatLocalTime(stop.visit.startTimeLocal)}
          {stop.state !== 'upcoming' && <span className="ml-2 font-normal">{t(`todayRoute.state.${stop.state}`)}</span>}
        </p>
        <p className="font-bold">{stop.visit.customerName}</p>
        {stop.address && <p className="text-sm">{stop.address}</p>}
      </div>
      <div className="flex gap-2">
        {url && (
          <a
            className="btn btn-secondary"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('todayRoute.navigateTo', { customer: stop.visit.customerName })}
          >
            {t('todayRoute.navigate')}
          </a>
        )}
        <Link className="btn btn-secondary" to={href}>
          {t('todayRoute.openVisit')}
        </Link>
      </div>
    </li>
  );
}
